import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { ApplicationPaths } from './modules/login/api-authorization.constants';
import { LoginComponent } from './modules/login/components/login.component';
import { LogoutComponent } from './modules/login/components/logout.component';
import { PermissionGuard } from './core/guards/permission.guard';
import { PermissionResource } from './core/constants/permissions.constants';

const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
  },
  { path: ApplicationPaths.Login, component: LoginComponent },
  { path: ApplicationPaths.LoginCallback, component: LoginComponent },
  { path: ApplicationPaths.LogOut, component: LogoutComponent },
  { path: ApplicationPaths.LoggedOut, component: LogoutComponent },
  { path: ApplicationPaths.LogOutCallback, component: LogoutComponent },
  {
    path: 'documents',
    loadChildren: () =>
      import('./modules/documents/documents.module').then((m) => m.DocumentsModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionResource: PermissionResource.Documents },
  },
  {
    path: 'workspaces',
    loadChildren: () =>
      import('./modules/workspaces/workspaces.module').then((m) => m.WorkspacesModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionResource: PermissionResource.Workspaces },
  },
  {
    path: 'playbooks',
    loadChildren: () =>
      import('./modules/playbooks/playbooks.module').then((m) => m.PlaybooksModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionResource: PermissionResource.Playbooks },
  },
  {
    path: 'clauses',
    loadChildren: () =>
      import('./modules/clauses/clauses.module').then((m) => m.ClauseLibraryModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionResource: PermissionResource.Clauses },
  },
  {
    path: 'questions',
    loadChildren: () =>
      import('./modules/questions/questions.module').then((m) => m.QuestionsModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionResource: PermissionResource.Questions },
  },
  {
    path: 'document-types',
    loadChildren: () =>
      import('./modules/doc-types/doc-types.module').then((m) => m.DocTypesModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionResource: PermissionResource.DocumentTypes },
  },
  {
    path: 'metadata',
    loadChildren: () => import('./modules/metadata/metadata.module').then((m) => m.MetadataModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionResource: PermissionResource.ExtractionMetadata },
  },
  {
    path: 'counterparties',
    loadChildren: () =>
      import('./modules/counterparties/counterparties.module').then((m) => m.CounterpartiesModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionResource: PermissionResource.Counterparties },
  },
  {
    path: 'baseline-documents',
    loadChildren: () =>
      import('./modules/baseline-documents/baseline-documents.module').then(
        (m) => m.BaselineDocumentsModule,
      ),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionResource: PermissionResource.BaselineDocuments },
  },
  {
    path: 'team',
    canActivate: [AuthGuard],
    canActivateChild: [PermissionGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'users' },
      {
        path: 'users',
        loadChildren: () => import('./modules/users/users.module').then((m) => m.UsersModule),
        data: { permissionResources: [PermissionResource.Users, PermissionResource.AccessRoles] },
      },
      {
        path: 'groups',
        loadChildren: () => import('./modules/groups/groups.module').then((m) => m.GroupsModule),
        data: { permissionResource: PermissionResource.Groups },
      },
      {
        path: 'team-roles',
        loadChildren: () =>
          import('./modules/team-roles/team-roles.module').then((m) => m.TeamRolesModule),
        data: { permissionResource: PermissionResource.TeamRoles },
      },
      {
        path: 'access-roles',
        loadChildren: () =>
          import('./modules/access-roles/access-roles.module').then((m) => m.AccessRolesModule),
        data: { permissionResource: PermissionResource.AccessRoles },
      },
      {
        path: 'permissions',
        loadChildren: () =>
          import('./modules/permissions/permissions.module').then((m) => m.PermissionsModule),
        data: { permissionResource: PermissionResource.Permissions },
      },
    ],
  },
  {
    path: 'templates',
    loadChildren: () =>
      import('./modules/templates/templates.module').then((m) => m.TemplatesModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: { permissionResource: PermissionResource.Templates },
  },
  {
    path: 'profile',
    loadChildren: () => import('./modules/profile/profile.module').then((m) => m.ProfileModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'access-restricted',
    loadChildren: () =>
      import('./modules/access-restricted/access-restricted.module').then(
        (m) => m.AccessRestrictedModule,
      ),
  },
  { path: '', redirectTo: '/login', pathMatch: 'full' },
  {
    path: '**',
    redirectTo: 'login',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
